"use client";

import { useCallback, useState, useSyncExternalStore } from "react";
import { useHydrated } from "@/lib/hooks/useHydrated";

/**
 * Last parsed value per key. `useSyncExternalStore` re-reads the snapshot on
 * every render, so parsing has to be memoized or React would see a new object
 * each time and loop forever.
 */
const cache = new Map<string, { raw: string | null; value: unknown }>();
const listeners = new Map<string, Set<() => void>>();

let storageBound = false;

/** Keep the cache honest when another tab writes to the same key. */
function bindStorageEvent(): void {
  if (storageBound || typeof window === "undefined") return;
  storageBound = true;
  window.addEventListener("storage", (event) => {
    if (event.key == null) {
      // `localStorage.clear()` in another tab.
      cache.clear();
      listeners.forEach((set) => set.forEach((notify) => notify()));
      return;
    }
    cache.delete(event.key);
    listeners.get(event.key)?.forEach((notify) => notify());
  });
}

function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    /* storage unavailable (private mode, blocked cookies) */
    return null;
  }
}

function readValue<T>(key: string, initial: T): T {
  const raw = readRaw(key);
  const cached = cache.get(key);
  if (cached && cached.raw === raw) return cached.value as T;

  let value = initial;
  if (raw != null) {
    try {
      value = JSON.parse(raw) as T;
    } catch {
      /* malformed JSON — fall back to the default */
    }
  }
  cache.set(key, { raw, value });
  return value;
}

/**
 * State that mirrors a JSON value in localStorage.
 *
 * SSR-safe: `initial` is the server snapshot, so the markup matches, and React
 * swaps in the stored value as part of hydration (no cascading effect, and no
 * chance of the default overwriting what is stored). The third tuple element
 * reports whether hydration has completed — gate any "not found" decisions on
 * it so client-only data isn't treated as missing before storage has been read.
 *
 * Writes from another tab are picked up through the `storage` event.
 */
export function useLocalStorageState<T>(
  key: string,
  initial: T,
): [T, (value: T | ((prev: T) => T)) => void, boolean] {
  // Pin the first `initial` so callers may pass a fresh literal each render.
  const [initialValue] = useState(() => initial);
  const hydrated = useHydrated();

  const subscribe = useCallback((onChange: () => void) => {
    bindStorageEvent();
    const set = listeners.get(key) ?? new Set();
    set.add(onChange);
    listeners.set(key, set);
    return () => {
      set.delete(onChange);
      if (set.size === 0) listeners.delete(key);
    };
  }, [key]);

  const value = useSyncExternalStore(
    subscribe,
    () => readValue(key, initialValue),
    () => initialValue,
  );

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      const prev = readValue(key, initialValue);
      const resolved =
        typeof next === "function" ? (next as (p: T) => T)(prev) : next;
      if (Object.is(resolved, prev)) return;

      const json = JSON.stringify(resolved);
      let persisted = true;
      try {
        window.localStorage.setItem(key, json);
      } catch {
        /* quota / unavailable storage — keep the value in memory only */
        persisted = false;
      }
      // Pair the value with whatever is actually stored, so the next snapshot
      // serves this value instead of re-parsing (and reverting) on failure.
      cache.set(key, { raw: persisted ? json : readRaw(key), value: resolved });
      listeners.get(key)?.forEach((notify) => notify());
    },
    [key, initialValue],
  );

  return [value, set, hydrated];
}
